import { View, SxProp, useDripsyTheme, Pressable, FlatList, SafeAreaView, ScrollView, ActivityIndicator } from 'dripsy';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import React, { useContext, useEffect, useState } from 'react';
import { formatAmountByDecimal } from '@/features/account-management/utils';
import { H6, TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { PageItem } from '@/features/earn/components/page/PageItem';
import { UniInput } from '@/design-system/components/inputs';
import { Icon } from '@/design-system/components/icons/Icon';
import { UniIconButton } from '@/design-system/components/buttons';
import { RootStackRoutes } from 'types';
import { AccountManagementStackRoutes } from '@/features/account-management/account.navigator';
import { CurrenciesRadios, CurrenciesValues } from '@/features/account-management/options';
import { PageContainerNative } from '@/features/account-management/screens/page/PageContainerNative';
import { useListNative } from '@/features/account-management/screens/list/hooks/useListNative.hook';
import { useListAccounts } from '@/features/account-management/screens/list/hooks/useListAccounts.hook';
import { AccountDetailsTabRoutes } from '@/features/account-management/screens/details/type';
import { keyGenerator } from '@/utils/keyGenerator';
import { LayoutContext } from 'contexts/LayoutContext';
import { CurrencySelector } from '@/design-system/components/currencySelector/CurrencySelector';
import { DownloadTransactionReportStackRoutes } from '@/features/account-management/screens/download-transaction-report/type';

export interface AccountListProps {}

export const AccountListMobileScreen: React.FC<AccountListProps> = () => {
  const navigation = useNavigation();
  const navigationAny = useNavigation<any>();
  const { theme } = useDripsyTheme();
  const layout = useContext(LayoutContext);
  const { t } = useTranslation('account-management');

  const [currency, setCurrency] = useState('HKD');
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<string | undefined>('ALL');
  const [actionNew, setActionNew] = useState<boolean>(false);

  const { accounts, isLoading, nextToken, preToken, next, prev, refetch, clearCurrentPageToken, createAccount } = useListAccounts({
    search: search,
    pageSize: 10
  });

  const onAccountPressed = (account: any, tab?: number) => {
    navigation.navigate(RootStackRoutes.AccountManagement, {
      screen: AccountManagementStackRoutes.Details,
      params: {
        screen: tab == 1 ? AccountDetailsTabRoutes.Card : AccountDetailsTabRoutes.Account,
        account: tab == 1 ? account?.cardAccountId : account?.id,
        record: account,
        tab: tab
      }
    });
  };

  const isNegative = (item: string) => {
    return item.startsWith('-');
  };

  const { bottomSheetFilterProps, filter } = useListNative({
    currencies: CurrenciesRadios
    // status: StatusFilters
  });
  useEffect(() => {
    setCurrency(filter?.currency ? filter?.currency[0] : 'HKD');
    let statusFlag = filter?.status ? filter?.status[0] : 'ALL';
    setStatus(statusFlag == 'ALL' ? undefined : statusFlag);
  }, [filter]);

  const [enableScrollViewScroll, setEnableScrollViewScroll] = useState(true);

  const [accountsData, setAccountsData] = useState([]);
  useEffect(() => {
    let accountsFlag = accountsData;
    accounts.forEach(
      (account) =>
        (accountsFlag.length && !accountsFlag.filter((af) => af?.id == account?.id).length && accountsFlag.push(account)) ||
        (!accountsFlag.length && accountsFlag.push(account))
    );
    setAccountsData(accountsFlag);
  }, [accounts, accountsData]);

  const flatlistHight = layout.dimensions.height - 216;

  return (
    <SafeAreaView sx={styles.containerSaft}>
      <ScrollView sx={styles.scrollContent} nestedScrollEnabled scrollEnabled={enableScrollViewScroll}>
        <View sx={styles.container}>
          <PageContainerNative>
            <H6 bold sx={{ mt: '$3', px: '$4', color: '$white' }}>
              {t('card-account-management::account-page.title--account-management')}
            </H6>
          </PageContainerNative>
          <PageItem sx={styles.card}>
            <View sx={{ flexDirection: 'row', justifyContent: 'space-between', gap: 9, px: '$4', alignItems: 'center' }}>
              <View sx={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                <TextTitle semiBold>{t('card-account::landing-page.title--select-account')}</TextTitle>
                {/* <Pressable
                  onPress={() => {
                    navigation.navigate(AccountManagementStackRoutes.ListBottomSheetFilter, bottomSheetFilterProps);
                  }}>
                  <Icon
                    name="activityHistoryFilter"
                    color={bottomSheetFilterProps.filteringNumber > 0 ? '$hyperlink' : undefined}
                    themeColor={bottomSheetFilterProps.filteringNumber > 0 ? '$hyperlink' : undefined}
                  />
                  {bottomSheetFilterProps.filteringNumber > 0 && <View sx={styles.productFilterIconNumber} />}
                </Pressable> */}
                <CurrencySelector options={CurrenciesValues} onValueChange={(value) => setCurrency(value)} />
              </View>
              <View sx={{ flexDirection: 'row', gap: '$1' }}>
                <Pressable
                  sx={{
                    ...styles.productFilterNativeIconBtn,
                    borderColor: '$borderButton'
                  }}
                  onPress={() =>
                    navigation.navigate(RootStackRoutes.AccountManagement, {
                      screen: AccountManagementStackRoutes.NewAccount,
                      params: {
                        func: (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) =>
                          createAccount(data, onSuccess, onError),
                        refresh: refetch
                      }
                    })
                  }>
                  <Icon name="addPayee" />
                </Pressable>
                <Pressable
                  sx={{
                    ...styles.productFilterNativeIconBtn,
                    borderColor: '$borderButton'
                  }}
                  onPress={() =>
                    navigationAny.navigate(RootStackRoutes.AccountManagement, {
                      screen: AccountManagementStackRoutes.DownloadTransactionReport,
                      params: {
                        screen: DownloadTransactionReportStackRoutes.DownloadAccountTransactionReport
                      }
                    })
                  }>
                  <Icon name="exportsymbol" width={24} height={24} />
                </Pressable>
              </View>
            </View>
            <View
              sx={styles.table}
              onStartShouldSetResponderCapture={() => {
                setEnableScrollViewScroll(false);
              }}>
              <View sx={styles.operations}>
                <View sx={styles.filters}>
                  <UniInput
                    sx={styles.filter_search}
                    size="default"
                    prefixIcon="search"
                    placeholder={t('card-account-management::account-page.text--search-by-account-name-no')}
                    onValueChange={(value) => {
                      setAccountsData([]);
                      clearCurrentPageToken();
                      setSearch(value);
                    }}
                  />
                </View>
              </View>
              <View sx={{ height: flatlistHight }}>
                <FlatList
                  data={accountsData}
                  renderItem={({ item }) => {
                    return (
                      <Pressable onPress={(e) => onAccountPressed(item)} sx={{ marginX: '$4', marginY: '$2' }}>
                        <View sx={{ padding: '$4', gap: 20, borderRadius: 10, boxShadow: '2px 4px 20px rgba(0, 0, 0, 0.07)' }}>
                          <View
                            sx={{
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: '$3',
                              width: '100%'
                            }}>
                            <View>
                              <TextBody bold sx={{ mb: '$1' }}>
                                {item?.name}
                              </TextBody>
                              <TextCaption>{item?.accountNumber}</TextCaption>
                            </View>
                            <View sx={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                              {/* <Icon
                              name="dotsymbol"
                              color={
                                item.status === AccountStatus.ACTIVE
                                  ? (theme?.colors?.$success as string)
                                  : (theme?.colors?.$danger as string)
                              }
                            />
                            <TextCaption success={item.status === AccountStatus.ACTIVE} danger={item.status === AccountStatus.INACTIVE}>
                              {StatusFilters.find((el) => el.value === item.status)?.label}
                            </TextCaption> */}
                              {item?.cardAccountId && <UniIconButton icon="card" onPress={(e) => onAccountPressed(item, 1)} />}
                            </View>
                          </View>
                          <View sx={{ gap: 8 }}>
                            <View sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                              <TextBody sx={{ color: '#666666' }}>{t('card-account-management::account-page.text--value')}</TextBody>
                              <TextBody semiBold sx={{ color: '#305992' }}>
                                {formatAmountByDecimal({
                                  value: currency === 'HKD' ? item?.valueHkd : item?.valueUsd,
                                  suffix: currency,
                                  decimals: 2
                                })}
                              </TextBody>
                            </View>
                            {/* <View sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                            <TextBody sx={{ color: '#666666' }}>Assets</TextBody>
                            <TextBody>1,000,000.00 HKD</TextBody>
                          </View>
                          <View sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                            <TextBody sx={{ color: '#666666' }}>Liabilities</TextBody>
                            <TextBody>100,000.00 HKD [DR]</TextBody>
                          </View> */}
                          </View>
                        </View>
                      </Pressable>
                    );
                  }}
                  scrollEnabled={true}
                  showsVerticalScrollIndicator={false}
                  sx={{ flex: 1, maxHeight: flatlistHight }}
                  keyExtractor={(_, index) => keyGenerator(`account-list-${index}`)}
                  onEndReachedThreshold={0.05}
                  onEndReached={() => {
                    if (!isLoading) {
                      if (nextToken) next();
                    }
                  }}
                />
                {isLoading && (
                  <View sx={{ padding: '$5' }}>
                    <ActivityIndicator size="large" color={theme.colors?.$disable || '#F0F0F0'} />
                  </View>
                )}
              </View>
            </View>
            {/* <View sx={{ alignItems: 'flex-end', px: '$4' }}>
              <TokenPagination prevPageToken={preToken} nextPageToken={nextToken} onNextPage={next} onPrevPage={prev} />
            </View> */}
          </PageItem>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

enum StyleName {
  containerSaft = 'containerSaft',
  scrollContent = 'scrollContent',
  container = 'container',
  card = 'card',
  table = 'table',
  operations = 'operations',
  filters = 'filters',
  filter_search = 'filter_search',
  filter_status = 'filter_status',
  btn_new = 'btn_new',
  productFilterNativeIconBtn = 'productFilterNativeIconBtn',
  productFilterIconNumber = 'productFilterIconNumber'
}

const styles: Record<StyleName, SxProp> = {
  containerSaft: {
    flex: 1,
    flexDirection: 'column',
    backgroundColor: '$backgroundSecondary'
  },
  scrollContent: {
    flex: 1,
    height: '100%',
    backgroundColor: '$backgroundSecondary'
  },
  container: {
    // margin: '$4',
    flexDirection: 'column',
    height: '100%'
  },
  card: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    // padding: '$4',
    marginTop: '$4',
    gap: '$4',
    borderRadius: 15
  },
  table: {
    flexDirection: 'column',
    gap: '$4'
  },
  operations: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    px: '$4',
    zIndex: 1
  },
  filters: {
    flexDirection: 'row',
    gap: '$5',
    width: '100%'
  },
  filter_search: {
    width: '100%'
  },
  filter_status: {
    width: '$52'
  },
  btn_new: {
    width: '$50'
  },
  productFilterNativeIconBtn: {
    height: '$6',
    width: '$6',
    justifyContent: 'center',
    alignItems: 'center'
  },
  productFilterIconNumber: {
    position: 'absolute',
    right: 0,
    width: '$2',
    height: '$2',
    borderWidth: 1,
    borderRadius: 100,
    borderColor: '$hyperlink',
    backgroundColor: '$hyperlink',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 100
  }
};
